/**
 * EstimateEdit — staff view/edit of a submitted project's estimate.
 *
 * For each room: the customer's inputs, then each line with the internal LABOR
 * cost, the price we'd charge, and a per-line MULTIPLIER (default 1.0 — unchanged
 * lines stay 1.0). "Send" applies the non-1.0 multipliers per room (POST
 * /admin/estimates/:id/edit). engine_output stays immutable; edits write
 * sent_output + an audit row and flip the estimate to SENT. Staff-only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAdminProject,
  editEstimate,
  sendEstimate,
  getMe,
  type AdminProject,
  type LineMultiplier,
} from '../../lib/api';
import { ProjectMedia } from '../../components/ProjectMedia';

function dollars(cents: number | null): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function EstimateEdit(): JSX.Element {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [project, setProject] = useState<AdminProject | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  // Multipliers as strings, keyed by `${estimateId}|${service}|${code}`. Empty = 1.0.
  const [mult, setMult] = useState<Record<string, string>>({});
  const [reason, setReason] = useState('');

  useEffect(() => {
    getMe()
      .then((me) => {
        const ok = me.role === 'ADMINISTRATOR' || me.role === 'ESTIMATOR';
        setAllowed(ok);
        if (ok && projectId) return getAdminProject(projectId).then(setProject);
        return undefined;
      })
      .catch(() => {
        setAllowed(false);
        setError('We couldn\u2019t load this project.');
      });
  }, [projectId]);

  const engineTotal = useMemo(
    () => (project?.rooms ?? []).reduce((s, r) => s + (r.engineTotalCents ?? 0), 0),
    [project],
  );

  // Baseline cost = sum of line costs (labor + materials, pre-markup). Internal.
  const costTotal = useMemo(
    () =>
      (project?.rooms ?? []).reduce(
        (s, r) => s + r.lines.reduce((ls, l) => ls + (l.lineCostCents ?? 0), 0),
        0,
      ),
    [project],
  );

  // Labor only = sum of crew-time labor across all rooms (no materials, no
  // markup). A quick read on the whole-project contractor labor.
  const laborTotal = useMemo(
    () =>
      (project?.rooms ?? []).reduce(
        (s, r) => s + r.lines.reduce((ls, l) => ls + (l.laborCostCents ?? 0), 0),
        0,
      ),
    [project],
  );

  function keyFor(estimateId: string, service: string, code: string): string {
    return `${estimateId}|${service}|${code}`;
  }

  function parsedMult(k: string): number {
    const raw = mult[k];
    if (raw == null || raw.trim() === '') return 1;
    const n = Number(raw);
    return Number.isNaN(n) || n <= 0 ? 1 : n;
  }

  // Live "new price" preview for a line given its current price + multiplier.
  function previewPrice(basePrice: number | null, k: string): number | null {
    if (basePrice == null) return null;
    return Math.round(basePrice * parsedMult(k));
  }

  // Project-wide sent total preview (applying current multipliers).
  const previewTotal = useMemo(() => {
    if (!project) return 0;
    let total = 0;
    for (const room of project.rooms) {
      for (const line of room.lines) {
        const k = keyFor(room.estimateId, line.moduleServiceLine, line.moduleCode);
        total += previewPrice(line.linePriceCents, k) ?? 0;
        total += line.extraFeeCents ?? 0;
      }
    }
    return total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, mult]);

  async function send(): Promise<void> {
    if (!project) return;
    setSaving(true);
    setError(null);
    try {
      // First apply any price edits per room (lines left at 1.0 are untouched).
      for (const room of project.rooms) {
        const lineMultipliers: LineMultiplier[] = [];
        for (const line of room.lines) {
          const k = keyFor(room.estimateId, line.moduleServiceLine, line.moduleCode);
          const m = parsedMult(k);
          if (m === 1) continue; // unchanged
          lineMultipliers.push({
            moduleServiceLine: line.moduleServiceLine,
            moduleCode: line.moduleCode,
            multiplier: m,
          });
        }
        if (lineMultipliers.length > 0) {
          await editEstimate(room.estimateId, {
            ...(reason.trim() ? { reason: reason.trim() } : {}),
            lineMultipliers,
          });
        }
      }
      // Then send: this works even with ZERO edits — it marks every sendable
      // room SENT, flips the project to SENT, and emails the customer.
      await sendEstimate(project.projectId);
      setDone(true);
    } catch {
      setError('We couldn\u2019t send the estimate. Try again.');
      setSaving(false);
    }
  }

  if (allowed === null) {
    return <div className="flow flow--single"><div className="stage-card"><p className="stage-card__lede">Checking access&hellip;</p></div></div>;
  }
  if (!allowed) {
    return (
      <div className="flow flow--single"><div className="stage-card">
        <h1>Not available</h1>
        <div className="q-actions"><button type="button" className="pb-btn" onClick={() => navigate('/dashboard')}>Back</button></div>
      </div></div>
    );
  }
  if (done) {
    return (
      <div className="flow flow--single"><div className="stage-card">
        <div className="pb-note pb-note--good" role="status">Estimate sent.</div>
        <h1>Sent</h1>
        <p className="stage-card__lede">The customer can now see their total. The engine&rsquo;s original is kept for the record.</p>
        <div className="q-actions" style={{ flexDirection: 'column' }}>
          <button type="button" className="pb-btn pb-btn--lg" onClick={() => navigate(`/admin/bids/${projectId}`)}>Bid the labor out</button>
          <button type="button" className="pb-btn pb-btn--ghost" onClick={() => navigate('/admin/leads')}>Back to the queue</button>
        </div>
      </div></div>
    );
  }
  if (!project) {
    return <div className="flow flow--single"><div className="stage-card"><p className="stage-card__lede">Loading&hellip;</p></div></div>;
  }

  return (
    <div className="flow flow--single">
      <div className="stage-card">
        <div className="progress__count">EDIT &amp; SEND</div>
        <h1>{project.name ?? 'Project'}</h1>
        <p className="stage-card__lede">
          {project.budgetLabel ? `Budget: ${project.budgetLabel}. ` : ''}
          {project.timelineLabel ? `Timeline: ${project.timelineLabel}. ` : ''}
        </p>
        <div className="pb-surface" style={{ marginBottom: 'var(--pb-3)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span className="estimate__hint">Labor only (whole project)</span>
            <span className="pb-option__label">{dollars(laborTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
            <span className="estimate__hint">Baseline cost (labor + materials, internal)</span>
            <span className="pb-option__label">{dollars(costTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
            <span className="estimate__hint">Suggested customer price</span>
            <span className="pb-option__label">{dollars(engineTotal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
            <span className="estimate__hint">Price with your multipliers (what the customer sees)</span>
            <span className="pb-option__label" style={{ color: 'var(--pb-chalk)' }}>{dollars(previewTotal)}</span>
          </div>
        </div>

        {project.rooms.map((room) => (
          <div key={room.estimateId} className="pb-surface" style={{ marginBottom: 'var(--pb-3)' }}>
            <div className="pb-option__label" style={{ marginBottom: 'var(--pb-2)' }}>
              {room.label ?? 'Room'} <span className="estimate__hint">({room.serviceLine})</span>
            </div>

            {/* Labor only — the room's total crew-time cost, at a glance. A quick
                read on what a contractor would be paid for this room's scope. */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: 'var(--pb-2)',
                marginBottom: 'var(--pb-2)',
                background: 'var(--pb-rule-soft)',
                borderRadius: '6px',
              }}
            >
              <span className="pb-option__label">Labor only (this room)</span>
              <span className="pb-option__label">
                {dollars(room.lines.reduce((s, l) => s + (l.laborCostCents ?? 0), 0))}
              </span>
            </div>

            {/* What the customer actually answered (read-only). Captured at
                create time; present on estimates created going forward. */}
            {room.answerLabels && room.answerLabels.length > 0 ? (
              <div style={{ marginBottom: 'var(--pb-3)' }}>
                <div className="progress__count" style={{ marginBottom: 'var(--pb-2)' }}>
                  WHAT THE CUSTOMER TOLD US
                </div>
                {room.answerLabels.map((qa, i) => (
                  <div key={i} style={{ marginBottom: 'var(--pb-1)' }}>
                    <div className="estimate__hint">{qa.prompt}</div>
                    <div className="pb-option__label" style={{ fontSize: 'var(--pb-size-small)' }}>
                      {qa.answers.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            {/* Customer inputs (derived digest — product/tier/measurements) */}
            {room.answers.length > 0 ? (
              <div style={{ marginBottom: 'var(--pb-2)' }}>
                {room.answers.map((a) => (
                  <div key={a.label} className="estimate__hint">
                    {a.label}: <strong>{a.value}</strong>
                  </div>
                ))}
              </div>
            ) : null}

            {/* Header row. LABOR = crew time; COST = labor + materials (baseline);
                PRICE = suggested customer price; NEW = price after the multiplier. */}
            <div style={{ display: 'flex', gap: 'var(--pb-1)', padding: '2px 0', fontSize: '0.68rem', color: 'var(--pb-ink-soft)' }}>
              <span style={{ flexGrow: 1 }}>ITEM</span>
              <span style={{ width: '4rem', textAlign: 'right' }}>LABOR</span>
              <span style={{ width: '4rem', textAlign: 'right' }}>COST</span>
              <span style={{ width: '4rem', textAlign: 'right' }}>PRICE</span>
              <span style={{ width: '3.5rem', textAlign: 'center' }}>×</span>
              <span style={{ width: '4rem', textAlign: 'right' }}>NEW</span>
            </div>

            {room.lines.map((line) => {
              const k = keyFor(room.estimateId, line.moduleServiceLine, line.moduleCode);
              const preview = previewPrice(line.linePriceCents, k);
              return (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 'var(--pb-1)', padding: '4px 0' }}>
                  <span style={{ flexGrow: 1, fontSize: 'var(--pb-size-small)' }}>{line.name}</span>
                  <span style={{ width: '4rem', textAlign: 'right', color: 'var(--pb-ink-soft)', fontSize: '0.78rem' }}>
                    {dollars(line.laborCostCents)}
                  </span>
                  <span style={{ width: '4rem', textAlign: 'right', color: 'var(--pb-ink-soft)', fontSize: '0.78rem' }}>
                    {dollars(line.lineCostCents)}
                  </span>
                  <span style={{ width: '4rem', textAlign: 'right', fontSize: '0.78rem' }}>
                    {dollars(line.linePriceCents)}
                  </span>
                  <input
                    className="pb-field"
                    style={{ width: '3.5rem', textAlign: 'center', padding: '4px' }}
                    inputMode="decimal"
                    placeholder="1.0"
                    value={mult[k] ?? ''}
                    onChange={(e) => setMult((m) => ({ ...m, [k]: e.target.value }))}
                    aria-label={`Multiplier for ${line.name}`}
                  />
                  <span style={{ width: '4rem', textAlign: 'right', fontSize: '0.78rem', color: 'var(--pb-chalk)' }}>
                    {dollars(preview)}
                  </span>
                </div>
              );
            })}

            {/* Per-room subtotals for each money column. */}
            {(() => {
              const laborSub = room.lines.reduce((s, l) => s + (l.laborCostCents ?? 0), 0);
              const costSub = room.lines.reduce((s, l) => s + (l.lineCostCents ?? 0), 0);
              const priceSub = room.lines.reduce((s, l) => s + (l.linePriceCents ?? 0), 0);
              const newSub = room.lines.reduce((s, l) => {
                const k = keyFor(room.estimateId, l.moduleServiceLine, l.moduleCode);
                return s + (previewPrice(l.linePriceCents, k) ?? 0);
              }, 0);
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--pb-1)',
                    padding: '6px 0 0',
                    marginTop: '4px',
                    borderTop: 'var(--pb-border) solid var(--pb-rule)',
                    fontWeight: 600,
                  }}
                >
                  <span style={{ flexGrow: 1, fontSize: '0.78rem' }}>Subtotal</span>
                  <span style={{ width: '4rem', textAlign: 'right', fontSize: '0.78rem' }}>{dollars(laborSub)}</span>
                  <span style={{ width: '4rem', textAlign: 'right', fontSize: '0.78rem' }}>{dollars(costSub)}</span>
                  <span style={{ width: '4rem', textAlign: 'right', fontSize: '0.78rem' }}>{dollars(priceSub)}</span>
                  <span style={{ width: '3.5rem' }} />
                  <span style={{ width: '4rem', textAlign: 'right', fontSize: '0.78rem', color: 'var(--pb-chalk)' }}>
                    {dollars(newSub)}
                  </span>
                </div>
              );
            })()}
          </div>
        ))}

        {/* Customer-provided photos/videos/links — staff can also add on their behalf. */}
        {projectId ? <ProjectMedia projectId={projectId} /> : null}

        <div className="subfield" style={{ marginTop: 'var(--pb-2)' }}>
          <label className="pb-label" htmlFor="edit-reason">Reason for edits (optional)</label>
          <input
            id="edit-reason"
            className="pb-field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. tile came in higher than the model"
          />
        </div>

        {error ? <div className="pb-note pb-note--flag" role="alert">{error}</div> : null}

        <div className="q-actions" style={{ flexDirection: 'column', marginTop: 'var(--pb-3)' }}>
          <button type="button" className="pb-btn pb-btn--lg" onClick={() => void send()} disabled={saving}>
            {saving ? 'Sending\u2026' : 'Send estimate'}
          </button>
          <button
            type="button"
            className="pb-btn pb-btn--ghost"
            onClick={() => navigate(`/admin/bids/${projectId}`)}
          >
            Bid the labor out
          </button>
          <button type="button" className="pb-btn pb-btn--ghost" onClick={() => navigate('/admin/leads')}>
            Back to the queue
          </button>
        </div>
      </div>
    </div>
  );
}
